import React from 'react';

/**
 * Renders a player number in a product's own Team-Portal typeface — the
 * font/color/size are supplied by the product's `soccer.player_number_preview`
 * metaobject (see backend: shopify_client.get_order_number_previews). Used
 * anywhere we'd normally show a generic Shopify product thumbnail for an
 * embellishment like "Stahls Team US 8-inch Number - Royal".
 *
 * Matches the white-on-white grey-background treatment used elsewhere in
 * the app (SmartProductImage): when the glyph color is r>210/g>210/b>210,
 * paint the container grey (#9ca3af) so the white number is visible.
 */
export default function NumberPreviewThumb({ preview, number, size = 48, borderColor = '#e5e7eb' }) {
    if (!preview) return null;
    // The 1.25x overscale we used to apply clips digit tops/bottoms at small
    // container sizes (~<50px), so the full number isn't visible. Scale to
    // the container size and let the font's natural ascender/descender space
    // pad inside — full digits always visible. Multi-digit numbers shrink
    // further so e.g. "47" fits in width = size.
    const digits = String(number || '').length;
    const widthScale = digits >= 2 ? 0.72 : 1.0;   // 2-digit fits, 3-digit shrinks more
    const glyphSize = Math.round(size * widthScale);
    const hex = (preview.color || '#000000').replace('#','').padEnd(6, '0');
    const r = parseInt(hex.substring(0, 2), 16) || 0;
    const g = parseInt(hex.substring(2, 4), 16) || 0;
    const b = parseInt(hex.substring(4, 6), 16) || 0;
    const isNearWhite = r > 210 && g > 210 && b > 210;
    return (
        <div style={{
            width: size,
            height: size,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: preview.color || '#111418',
            backgroundColor: isNearWhite ? '#9ca3af' : 'transparent',
            fontFamily: `'${preview.fontFamily}', sans-serif`,
            fontSize: glyphSize,
            lineHeight: 1,
            fontWeight: 400,
            fontVariantNumeric: 'tabular-nums',
            overflow: 'hidden',
            border: borderColor ? `1px solid ${borderColor}` : 'none',
            borderRadius: 4,
            boxSizing: 'border-box',
        }}>{number}</div>
    );
}

// Builds a CSS string with @font-face rules for every unique font in the
// preview map. Caller renders inside a <style>{...}</style> tag.
export function buildFontFaceCss(previews) {
    if (!previews) return '';
    const seen = new Set();
    const rules = [];
    for (const p of Object.values(previews)) {
        if (!p?.fontUrl || !p?.fontFamily || seen.has(p.fontFamily)) continue;
        seen.add(p.fontFamily);
        const fmt = (p.mimeType || '').includes('woff2') ? 'woff2'
                  : (p.mimeType || '').includes('woff')  ? 'woff'
                  : 'woff2';
        rules.push(`@font-face { font-family: '${p.fontFamily}'; src: url('${p.fontUrl}') format('${fmt}'); font-display: block; font-weight: 400; font-style: normal; }`);
    }
    return rules.join('\n');
}

// Extracts the printed Number attribute from a line item's customAttributes
// list. Same lookup the printout already uses.
export function getPrintedNumberAttr(customAttributes) {
    const list = customAttributes || [];
    for (const key of ['Number', 'number', '#']) {
        const f = list.find(a => a.key === key || a.key?.toLowerCase() === key.toLowerCase());
        if (f?.value) return f.value;
    }
    return null;
}

// Convenience — given a line item and a preview map (keyed by product GID),
// return the preview settings to use OR null. Caller is responsible for
// rendering NumberPreviewThumb only when this returns non-null AND a
// number is available.
export function getPreviewFor(lineItem, previews) {
    if (!previews) return null;
    const pid = lineItem?.variant?.product?.id;
    if (!pid) return null;
    return previews[pid] || null;
}
